/**
 * FormSituationPanel.jsx
 * Filter bar (sport/result/opponent/competition-scope) + the full match
 * history table for the Game Stats Dashboard. Used to also render a
 * "SITUATION" header, last-5-match averages and a competition-standings
 * list, but those duplicated the stats strip above it and the Competitions
 * tab elsewhere on Game Stats — removed 2026-09-27, keeping just the
 * filters (which the table below depends on) and the table itself.
 */

import React, { useState, useEffect, useMemo } from 'react';
import supabase from '../supabaseClient';
import MatchesCompetitionsReport, { DURATION_BUCKETS, HR_BUCKETS, CALORIE_BUCKETS } from './MatchesCompetitionsReport';
import { todayLocal } from '../utils/dateLocal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const C = {
  bg: '#0d1117', card: '#161b22', border: 'rgba(255,255,255,0.18)',
  text: '#e6edf3', muted: '#a8b3bd', accent: '#3dbfb0',
  win: '#22c55e', loss: '#ef4444', draw: '#94a3b8',
  font: "'Inter', system-ui, sans-serif",
};

const MATCH_TYPES = ['match', 'competition', 'race_day'];

// ── Main panel ─────────────────────────────────────────────────────────────
export default function FormSituationPanel({ plans, userId, activeSport, demoMode = false, onOpenDetail, isMobile = false }) {
  const [scope, setScope] = useState('all'); // 'all' | 'competition'
  const [competitions, setCompetitions] = useState([]);
  const [filterResult,       setFilterResult]       = useState('');
  const [filterOpponent,     setFilterOpponent]     = useState('');
  // Full filter set per user request 2026-11-02: competition and format
  // reuse data already loaded here; duration/HR/calories use pre-set,
  // logical buckets (see MatchesCompetitionsReport.jsx) rather than free
  // min/max inputs — consistent with the rest of the app and far easier
  // to use on mobile than a pair of number fields.
  const [filterCompetitionId, setFilterCompetitionId] = useState('');
  const [filterFormat,       setFilterFormat]       = useState('');
  const [filterDuration,     setFilterDuration]     = useState('');
  const [filterHr,           setFilterHr]           = useState('');
  const [filterCalories,     setFilterCalories]     = useState('');
  // Lifted from MatchesCompetitionsReport (rendered `bare` below) so the
  // title + live count can sit at the very top of this one merged card,
  // instead of filters -> a second nested card with its own title+border.
  // See user feedback 2026-10-02.
  const [eventCount, setEventCount] = useState(0);

  useEffect(() => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/competitions`)
        .then(r => r.json()).then(d => setCompetitions(d.competitions || [])).catch(e => console.error('[FormSituationPanel] demo competitions load failed:', e));
      return;
    }
    if (!userId) return;
    let q = supabase.from('competitions').select('id,name,type,status,start_date,end_date,goal,goal_status')
      .eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('start_date', { ascending: false })
      .then(({ data }) => setCompetitions(data || []));
  }, [userId, demoMode, activeSport]);

  const allMatches = useMemo(() => {
    const todayStr = todayLocal();
    return plans
      .filter(p => MATCH_TYPES.includes(p.type) && p.result && (p.scheduled_date || '').slice(0, 10) <= todayStr)
      .sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));
  }, [plans]);

  const opponentOptions = useMemo(() => {
    const seen = new Set();
    return allMatches.filter(p => p.opponent_name && !seen.has(p.opponent_name) && seen.add(p.opponent_name))
      .map(p => p.opponent_name).sort();
  }, [allMatches]);
  // Only offer the sport's own competitions (already loaded above), and
  // only offer a Format filter when doubles has actually been played —
  // no point showing a Singles/Doubles choice to a squash player, for
  // instance, who never has a doubles option in the first place.
  const competitionOptions = useMemo(() => (
    [...competitions].sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
  ), [competitions]);
  const hasDoubles = useMemo(() => allMatches.some(p => p.match_format === 'doubles'), [allMatches]);
  const activeFilterCount = [filterResult, filterOpponent, filterCompetitionId, filterFormat, filterDuration, filterHr, filterCalories]
    .filter(Boolean).length;
  const clearAllFilters = () => {
    setFilterResult(''); setFilterOpponent(''); setFilterCompetitionId('');
    setFilterFormat(''); setFilterDuration(''); setFilterHr(''); setFilterCalories('');
  };
  const selStyle = {
    background: '#1a2236', border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.text, fontSize: 12, padding: '6px 10px', fontFamily: C.font, cursor: 'pointer',
  };

  if (allMatches.length === 0) return null;

  return (
    <div style={{
      background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
      border: '1.5px solid rgba(56,189,248,0.65)', borderRadius: 14, padding: '18px 18px 8px', marginBottom: 20,
    }}>
      {/* Filters for the match table below, incl. the all/competitions-only
          scope toggle — the "SITUATION" header, last-5-match averages and
          per-competition standings list were removed (2026-09-27): they
          duplicated the stats strip and Competitions tab already shown
          elsewhere on Game Stats. Only the filters + table stay here. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#38bdf8', fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>
          🏆 MATCHES & COMPETITIONS
        </h3>
        <span style={{ fontSize: 12, color: C.muted }}>{eventCount} events</span>
      </div>

      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', borderRadius: 20, padding: 3, marginBottom: 14, width: 'fit-content' }}>
        {[['all', 'All matches'], ['competition', 'Competitions only']].map(([val, label]) => (
          <button key={val} onClick={() => setScope(val)} style={{
            border: 'none', borderRadius: 16, padding: '5px 12px', fontSize: 11.5, fontWeight: 700,
            cursor: 'pointer', fontFamily: C.font,
            background: scope === val ? C.accent : 'transparent',
            color: scope === val ? '#04120f' : C.muted,
          }}>{label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {/* No sport filter here — full isolation (2026-10-18,
            PRODUCT-GUIDELINES.md §5) means this list is already scoped to
            the active sport ("not even in the selectors"); a dropdown
            offering other sports would contradict that. */}
        <select value={filterResult} onChange={e => setFilterResult(e.target.value)} style={selStyle}>
          <option value="">Any result</option>
          <option value="win">Win</option>
          <option value="draw">Draw</option>
          <option value="loss">Loss</option>
        </select>
        <select value={filterOpponent} onChange={e => setFilterOpponent(e.target.value)} style={selStyle}>
          <option value="">All opponents</option>
          {opponentOptions.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {competitionOptions.length > 0 && (
          <select value={filterCompetitionId} onChange={e => setFilterCompetitionId(e.target.value)} style={selStyle}>
            <option value="">All competitions</option>
            {competitionOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {hasDoubles && (
          <select value={filterFormat} onChange={e => setFilterFormat(e.target.value)} style={selStyle}>
            <option value="">Singles & doubles</option>
            <option value="singles">Singles</option>
            <option value="doubles">Doubles</option>
          </select>
        )}
        <select value={filterDuration} onChange={e => setFilterDuration(e.target.value)} style={selStyle}>
          {DURATION_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <select value={filterHr} onChange={e => setFilterHr(e.target.value)} style={selStyle}>
          {HR_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <select value={filterCalories} onChange={e => setFilterCalories(e.target.value)} style={selStyle}>
          {CALORIE_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        {activeFilterCount > 0 && (
          <button onClick={clearAllFilters}
            style={{ ...selStyle, color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)' }}>
            ✕ Clear {activeFilterCount}
          </button>
        )}
      </div>

      {/* Full match history, newest first by default (filters below narrow it
          down; a column header click can still re-sort). This is the front
          page's one match table; SessionsDashboard no longer renders a
          second, separate one below. */}
      <MatchesCompetitionsReport
        userId={userId}
        activeSport={activeSport}
        competitions={competitions}
        competitionOnly={scope === 'competition'}
        filterResult={filterResult}
        filterOpponent={filterOpponent}
        filterCompetitionId={filterCompetitionId}
        filterFormat={filterFormat}
        filterDuration={filterDuration}
        filterHr={filterHr}
        filterCalories={filterCalories}
        onOpenDetail={onOpenDetail}
        demoMode={demoMode}
        demoPlans={plans}
        bare
        onCount={setEventCount}
      />
    </div>
  );
}
